// The collection grid's behaviour: filters on ask, the SmartShip switch, quick
// add, and the options panel.
//
// No money is formatted here. Both price states are rendered server-side on the
// card and this only chooses which to show — a shop selling in eleven
// currencies should not have a second formatter in JavaScript that can disagree
// with the first.
(() => {
  const root = document.querySelector('[data-pruvit-coll]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ---- filters, in a drawer, closed until asked for ----------------------
  const filterToggle = root.querySelector('[data-pruvit-filter-toggle]');
  const filters = document.querySelector('[data-pruvit-filters]');
  let filtersLastFocus = null;

  const closeFilters = () => {
    if (!filters || filters.hidden) return;
    filters.classList.remove('is-open');
    setTimeout(() => {
      filters.hidden = true;
    }, 260);
    if (filterToggle) {
      filterToggle.setAttribute('aria-expanded', 'false');
      if (filtersLastFocus) filtersLastFocus.focus();
    }
  };

  if (filterToggle && filters) {
    filterToggle.addEventListener('click', () => {
      filtersLastFocus = filterToggle;
      filters.hidden = false;
      // In layout before the transition has anything to move from.
      void filters.offsetWidth;
      filters.classList.add('is-open');
      filterToggle.setAttribute('aria-expanded', 'true');
      const first = filters.querySelector('input, button, a');
      if (first) first.focus();
    });

    for (const closer of filters.querySelectorAll('[data-pruvit-filters-close]')) {
      closer.addEventListener('click', closeFilters);
    }

    // Facets are links in disguise: the form's state is the URL's state, so
    // applying them is a navigation. What it must not be is a navigation per
    // tick — this used to run on `change`, so the first box a shopper ticked
    // reloaded the page out from under them and choosing a second was
    // impossible. The drawer holds the selection; Show results commits it.
    const readFilters = () => {
      const params = new URLSearchParams();
      for (const input of filters.querySelectorAll('input:checked')) params.append(input.name, input.value);
      const sort = root.querySelector('[data-pruvit-sort]');
      if (sort && sort.value) params.set('sort_by', sort.value);
      return params;
    };

    for (const apply of filters.querySelectorAll('[data-pruvit-filters-apply]')) {
      apply.addEventListener('click', () => {
        const next = readFilters().toString();
        // Nothing chosen that was not already applied: close rather than
        // reload the same page, which would throw away the scroll position to
        // arrive somewhere identical.
        const now = new URLSearchParams(window.location.search);
        now.delete('page');
        if (next === now.toString()) return closeFilters();
        window.location.search = next;
      });
    }

    // Live count on the button, so the drawer says what it is about to do.
    const countLabel = () => {
      const chosen = filters.querySelectorAll('input:checked').length;
      for (const apply of filters.querySelectorAll('[data-pruvit-filters-apply]')) {
        const base = apply.dataset.label || (apply.dataset.label = apply.textContent.trim());
        apply.textContent = chosen ? `${base} (${chosen})` : base;
      }
    };
    filters.addEventListener('change', countLabel);
    countLabel();
  }

  const sort = root.querySelector('[data-pruvit-sort]');
  if (sort) {
    sort.addEventListener('change', () => {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', sort.value);
      window.location.search = params.toString();
    });
  }

  // ---- price states -------------------------------------------------------
  //
  // Each card carries up to three: the one-time price, the SmartShip price, and
  // the one-time price as it stands with a subscription in the cart. Two
  // switches choose between them — the SmartShip toggle, which the shopper
  // operates, and the cart, which they change by adding or removing a
  // subscription anywhere on the page.
  //
  // Both go through one function, because they are not independent. The cart
  // only affects the one-time side; turning SmartShip on shows the plan price
  // either way.
  const subToggle = root.querySelector('[data-pruvit-sub-toggle]');
  let joining = false;

  const apply = (on) => {
    for (const price of root.querySelectorAll('[data-pruvit-card-price]')) {
      const now = price.querySelector('[data-pruvit-now]');
      const was = price.querySelector('[data-pruvit-was]');
      const note = price.parentElement.querySelector('[data-pruvit-card-note]');
      // A product with no plan keeps its one-time price. The switch is about
      // what is available, not a promise that everything has it.
      const hasPlan = Boolean(price.dataset.onNow);
      const useOn = on && hasPlan;
      // Only where Liquid rendered the state. A VIP's price does not move with
      // the cart, and neither does a product outside the discounted
      // collection — for both, there is no join state and nothing to switch to.
      const useJoin = !useOn && joining && Boolean(price.dataset.joinNow);

      let nowText = price.dataset.offNow;
      let wasText = price.dataset.offWas;
      if (useOn) {
        nowText = price.dataset.onNow;
        wasText = price.dataset.onWas;
      } else if (useJoin) {
        nowText = price.dataset.joinNow;
        wasText = price.dataset.joinWas;
      }

      now.textContent = nowText;
      now.classList.toggle('is-cut', useOn || useJoin || price.dataset.offCut === '1');
      was.textContent = wasText || '';
      was.hidden = !wasText;
      if (note) {
        let noteText = note.dataset.offNote;
        if (useOn) noteText = note.dataset.onNote;
        else if (useJoin) noteText = note.dataset.joinNote;
        note.textContent = noteText || '';
        note.hidden = !noteText;
        note.classList.toggle(
          'pruvit-card__note--quiet',
          !useOn && !useJoin && price.dataset.offCut !== '1'
        );
        // The tick belongs to the savings note and no other. It is drawn by a
        // pseudo-element rather than markup precisely because the line above
        // replaces the contents, so the class is what has to keep step.
        const applied = useJoin || (!useOn && Boolean(note.dataset.offNote) && price.dataset.offCut === '1');
        note.classList.toggle('pruvit-card__note--applied', applied);
      }
    }
  };

  const smartShipOn = () => Boolean(subToggle && subToggle.getAttribute('aria-checked') === 'true');

  if (subToggle) {
    subToggle.addEventListener('click', () => {
      const on = subToggle.getAttribute('aria-checked') !== 'true';
      subToggle.setAttribute('aria-checked', String(on));
      apply(on);
    });
  }

  // The cart is asked rather than inferred from the event, because a removal
  // has to put the prices back as surely as an add takes them down, and the
  // only thing that knows either way is the cart itself. Liquid already
  // rendered the correct state for the cart as it stood, so this does nothing
  // until it actually changes.
  let lastJoining = null;
  const syncCart = async () => {
    try {
      const cart = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      joining = (cart.items || []).some((item) => item.selling_plan_allocation);
    } catch (e) {
      return;
    }
    if (joining === lastJoining) return;
    lastJoining = joining;
    apply(smartShipOn());
  };

  for (const event of ['pruvit:cart:added', 'shopify:cart:lines-update']) {
    document.addEventListener(event, syncCart);
  }

  // ---- adding ------------------------------------------------------------
  const add = async (id, button, sellingPlan) => {
    const label = button.querySelector('span');
    const original = label ? label.textContent : '';
    button.disabled = true;
    // The same spinner the Choose options button shows, in the same disc. The
    // label is left alone: the plus turning is the answer, and swapping the
    // words as well moves the button's width while the pointer is still on it.
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1, ...(sellingPlan ? { selling_plan: sellingPlan } : {}) }],
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error((payload && (payload.description || payload.message)) || pruvitT('addFailed', 'Could not add that.'));

      // Hand the confirmed line to the cart, the same event the buy box uses,
      // so the drawer draws the row without asking for the cart first.
      document.dispatchEvent(
        new CustomEvent('pruvit:cart:added', { detail: { items: payload.items || [], source: 'pruvit-collection' } })
      );
      document.dispatchEvent(
        new CustomEvent('shopify:cart:lines-update', { detail: { action: 'add', source: 'pruvit-buy' } })
      );
      const opener = document.querySelector('[data-pruvit-cart-open]');
      if (opener) opener.click();
      closePanel();
    } catch (e) {
      if (label) label.textContent = e.message;
      setTimeout(() => {
        if (label) label.textContent = original;
      }, 2400);
      return;
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };

  // ---- flavour swatches --------------------------------------------------
  /**
   * The swatches are a choice, not a legend: picking one puts that flavour's
   * pack in the card and carries the flavour into the options drawer.
   *
   * Pressing the chosen one again clears it. The product's own shot is stashed
   * on the image the first time it is replaced, so restoring it needs nothing
   * rendered ahead of time — and a card whose swatch cannot be un-picked is one
   * a shopper has no way back out of.
   */
  const chooseFlavor = (swatch) => {
    const card = swatch.closest('[data-pruvit-card]');
    if (!card) return;
    const image = card.querySelector('.pruvit-card__img:not(.pruvit-card__img--hover)');
    const wanted = swatch.dataset.pruvitSw;
    const undo = card.dataset.flavor === wanted;

    for (const other of card.querySelectorAll('[data-pruvit-sw]')) {
      other.setAttribute('aria-pressed', String(!undo && other === swatch));
    }

    if (undo) {
      delete card.dataset.flavor;
    } else {
      card.dataset.flavor = wanted;
    }

    if (!image) return;
    if (!image.dataset.ownImg) {
      image.dataset.ownImg = image.getAttribute('src') || '';
      image.dataset.ownSrcset = image.getAttribute('srcset') || '';
    }
    const src = undo ? image.dataset.ownImg : swatch.dataset.img;
    const srcset = undo ? image.dataset.ownSrcset : swatch.dataset.srcset;
    // A flavour with no shot of its own leaves the picture alone rather than
    // blanking the card.
    if (!src) return;
    image.setAttribute('src', src);
    if (srcset) image.setAttribute('srcset', srcset);
    else image.removeAttribute('srcset');
  };

  // Warm the swap before it is asked for, so the click does not start with an
  // empty box. The browser drops the request if it is already cached.
  root.addEventListener(
    'pointerenter',
    (event) => {
      const swatch = event.target.closest && event.target.closest('[data-pruvit-sw][data-img]');
      if (!swatch || swatch.dataset.warmed) return;
      swatch.dataset.warmed = '1';
      const warm = new Image();
      if (swatch.dataset.srcset) warm.srcset = swatch.dataset.srcset;
      warm.src = swatch.dataset.img;
    },
    true
  );

  root.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-pruvit-sw]');
    if (swatch) return chooseFlavor(swatch);
    const quick = event.target.closest('[data-pruvit-quick-add]');
    if (quick) return add(quick.dataset.pruvitQuickAdd, quick);
    const choose = event.target.closest('[data-pruvit-choose]');
    if (choose) {
      const card = choose.closest('[data-pruvit-card]');
      return openPanel(choose.dataset.pruvitChoose, choose, card && card.dataset.flavor);
    }
  });

  // ---- the options panel -------------------------------------------------
  const panel = document.querySelector('[data-pruvit-opt]');
  const body = panel && panel.querySelector('[data-pruvit-opt-body]');
  const cache = new Map();
  let lastFocus = null;

  function closePanel() {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    // Wait out the slide rather than yanking it off screen.
    setTimeout(() => {
      panel.hidden = true;
    }, 260);
    if (lastFocus) lastFocus.focus();
  }

  const esc = (v) =>
    String(v == null ? '' : v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /**
   * The drawer is the buy box, compact.
   *
   * /products/{handle}?view=quick is a bare template that renders the same
   * snippet the buy box block renders, so the drawer gets the real flavour
   * discs, the real size pills with their savings, the real one-time/subscribe
   * radiogroup and the real total-in-the-button — rather than a second set of
   * controls built to look like them, which is what was here before.
   *
   * pruvit-buy.js drives anything at [data-pruvit-buy] and initialises on
   * shopify:section:load, so injecting the markup and firing that event hands
   * the whole thing over. No pricing, no variant matching and no money
   * formatting in this file at all now.
   */
  // Which request is the current one. A shopper who gets bored and clicks a
  // second card should get the second card, so a response that arrives after
  // its click has been superseded is dropped rather than raced into the panel.
  let openToken = 0;

  function showPanel(markup, flavor) {
    body.innerHTML = markup;
    if (flavor) {
      const discs = body.querySelectorAll('[data-pruvit-opts="flavor"] button');
      const match = [...discs].find((d) => d.dataset.value === flavor);
      if (match) for (const disc of discs) disc.setAttribute('aria-pressed', String(disc === match));
    }
    panel.hidden = false;
    // In layout before the transition has anything to move from.
    void panel.offsetWidth;
    panel.classList.add('is-open');
    // Hand it to the buy box's own script, which initialises on this.
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
    watchFloor();
    const first = body.querySelector('button:not([disabled]), a');
    if (first) first.focus();
  }

  /**
   * Tell the footer whether it is holding the floor.
   *
   * CSS can stick an element but cannot say whether it is currently stuck, and
   * the rule above it should only appear when it is — an edge on something
   * sitting in its natural place reads as a divider between two things rather
   * than as a panel lifted over what is behind it.
   *
   * "Is there anything below still to scroll to" is the same question, and the
   * scroller can answer it. Watched on scroll and on resize, plus whenever the
   * form's own height changes: choosing a flavour with no 20-serving option
   * removes a row, and a footer that stayed lifted over nothing would be the
   * tell.
   */
  let floorObserver = null;
  function watchFloor() {
    const foot = body.querySelector('.pruvit-buy__foot');
    if (!foot) return;
    const update = () => {
      // A pixel of tolerance: fractional scroll heights are normal and would
      // otherwise leave the rule showing at the very bottom of the scroll.
      foot.classList.toggle('is-stuck', body.scrollTop + body.clientHeight < body.scrollHeight - 1);
    };
    body.addEventListener('scroll', update, { passive: true });
    if (floorObserver) floorObserver.disconnect();
    if (window.ResizeObserver) {
      floorObserver = new ResizeObserver(update);
      floorObserver.observe(body.firstElementChild || body);
    }
    update();
  }

  async function openPanel(handle, trigger, flavor) {
    if (!panel || !body) return;
    lastFocus = trigger;

    // Already fetched: no spinner at all. A spinner that appears for one frame
    // is a flicker, not feedback.
    if (cache.has(handle)) return showPanel(cache.get(handle), flavor);

    const token = ++openToken;
    // The wait shows on the button that was pressed. The panel stays shut until
    // there is something to put in it.
    if (trigger) {
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
    }
    const done = () => {
      if (!trigger) return;
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');
    };

    try {
      const response = await fetch(`${shopRoot()}products/${handle}?view=quick`);
      if (!response.ok) throw new Error('unavailable');
      cache.set(handle, await response.text());
      done();
      if (token !== openToken) return;
      return showPanel(cache.get(handle), flavor);
    } catch (e) {
      done();
      if (token !== openToken) return;
      // The drawer still opens, carrying the failure. Clearing the spinner and
      // showing nothing would read as a dead button.
      panel.hidden = false;
      void panel.offsetWidth;
      panel.classList.add('is-open');
      const wait = document.createElement('p');
      wait.className = 'pruvit-opt__wait';
      wait.textContent = pruvitT('productLoadFailed', 'Could not load this product.');
      body.replaceChildren(wait);
    }
  }


  if (panel) {
    // The buy box inside the drawer opens the cart itself. Leaving the drawer
    // up behind it would put two panels on screen over each other.
    document.addEventListener('pruvit:cart:added', (event) => {
      if (event.detail && event.detail.source === 'pruvit-collection') return;
      closePanel();
    });

    for (const closer of panel.querySelectorAll('[data-pruvit-opt-close]')) {
      closer.addEventListener('click', closePanel);
    }
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closePanel();
      closeFilters();
    });
  }
})();
